/**
 * AI-assisted question generation module constants.
 *
 * Bilgilig dashboard `question-generation` modülünden birebir port. Uçlar QG
 * backend'ine uyarlandı; review/passive/activate uçları backend hizalanınca aktif olur.
 */

import { DEFAULT_PER_PAGE } from '@/core/lib/pagination'

export const questionGenerationEndpoints = {
  // Generation batches (üretim grupları / versiyonlar). `kind` ALL ise köken filtresi eklenmez.
  // per_page açıkça gönderilir (C-07): yoksa BE default'u (10) FE niyetini (20) sessizce ezerdi.
  listBatches: (page: number, query: string, kind?: string) => {
    const filters = [`name:${encodeURIComponent(query)}`]
    // Backend query parser operator taşımıyor; liste değeri `column.in_()`
    // üzerinden exact filtreye düşer. `kind:INDIVIDUAL` ise default ilike olur.
    if (kind && kind !== 'ALL') filters.push(`kind:[${kind}]`)
    return `/generation?page=${page}&per_page=${DEFAULT_PER_PAGE}&filters=${filters.join(',')}` // GET
  },
  llmOptions: '/generation/llm-options', // GET (sağlayıcı + model isimleri, .env'den)
  createBatch: '/generation', // POST (multipart: documents + criteria)
  batchDetail: (id: string) => `/generation/${id}`, // GET
  updateDraft: (id: string) => `/generation/${id}`, // PATCH (yalnız DRAFT kriterleri)
  deleteBatch: (id: string) => `/generation/${id}`, // DELETE
  batchQuestions: (id: string) => `/generation/${id}/questions`, // GET
  passiveBatch: (id: string) => `/generation/${id}/passive`, // POST
  activateBatch: (id: string) => `/generation/${id}/activate`, // POST
  deliverBatch: (id: string) => `/generation/${id}/deliver`, // POST (Bilgilig'e aktar)
  // Per-question review actions
  reviewQuestion: (id: string) => `/generated-questions/${id}/review`, // PATCH
}

export const questionGenerationSuccessMessages = {
  generationStarted:
    'Soru üretimi başlatıldı. Tamamlanınca incelemeye hazır olacak.',
  draftSaved:
    'Taslak kaydedildi. Periyodik Üretim ekranında şablon olarak seçebilirsiniz.',
  draftUpdated: 'Taslak güncellendi.',
  questionApproved: 'Soru onaylandı ve havuza eklendi.',
  questionRejected: 'Soru reddedildi.',
  questionRevision: 'Soru revizyona gönderildi.',
  batchPassive: 'Üretim grubu pasife alındı.',
  batchActivated: 'Üretim grubu yeniden aktifleştirildi.',
  batchDeleted: 'Üretim grubu silindi.',
  batchDelivered: "Onaylanan sorular Bilgilig'e aktarıldı.",
}

/** Üretim grubunun Bilgilig'e teslim (import) durumu. docs/question_import. */
export const importStatusOptions = {
  NOT_IMPORTED: 'Aktarılmadı',
  PARTIAL: 'Kısmen Aktarıldı',
  IMPORTED: 'Aktarıldı',
} as const

/** Import durum rozeti renkleri (Ant Design Tag). */
export const importStatusColors = {
  NOT_IMPORTED: 'default',
  PARTIAL: 'orange',
  IMPORTED: 'green',
} as const

/** Soru zorluk seviyeleri (Bilgilig question modülü). */
export const questionDifficultyOptions = {
  VERY_EASY: 'Çok Kolay',
  EASY: 'Kolay',
  NORMAL: 'Normal',
  HARD: 'Zor',
  VERY_HARD: 'Çok Zor',
} as const

/** Oyun klasmanları (Bilgilig question modülü MatchModeEnum). */
export const questionModeOptions = {
  CASUAL: 'Eğlence',
  RANKED: 'Bilgi Ligi',
} as const

/** PDF 7.5 — sabit hassasiyet listesi. Serbest "diğer" için ek talimat alanı. */
export const sensitivityOptions = {
  RELIGIOUS: 'Dini içeriklerden kaçın',
  POLITICAL: 'Siyasi içeriklerden kaçın',
  AGE: 'Yaş uygunluğuna dikkat et',
  CULTURAL: 'Kültürel hassasiyetlere dikkat et',
} as const

/** PDF 7.6 — sabit dil/üslup listesi. */
export const languageToneOptions = {
  FUN: 'Eğlenceli dil',
  CHILD: 'Çocuklara uygun dil',
  FORMAL: 'Resmî dil',
  PLAIN: 'Sade ve anlaşılır dil',
  CONCISE: 'Kısa ve net format',
} as const

/** CHAT sağlayıcısı (üretim grubu başına seçilir). OpenRouter yalnız API key varsa listelenir. */
export const providerOptions = {
  ollama: 'Ollama (yerel)',
  openrouter: 'OpenRouter (bulut)',
} as const

/** Model kalite seviyesi temel etiketleri. Gerçek model adı .env'den (llm-options)
 * eklenir → "Zayıf (deepseek/deepseek-chat)". Yalnız OpenRouter'da gösterilir. */
export const modelTierBaseLabels = {
  weak: 'Zayıf',
  medium: 'Orta',
  strong: 'Güçlü',
} as const

/** Belge yükleme kısıtları (item: sadece PDF, 1 dosya, 100MB). */
export const MAX_DOCUMENT_MB = 100

export const generationWarnings = {
  strongModel:
    'Daha kaliteli/zor işlemler için "Güçlü" modeli seçin. Güçlü modeli yalnızca gerçekten ihtiyaç duyduğunuzda kullanın (daha yavaş ve maliyetli).',
  largeDocStrong:
    'Yüksek boyutlu dokümanlarda ihtiyaca göre "Güçlü" modeli kullanın.',
}

/** Üretim grubu (batch) durumları. PDF 12. */
export const batchStatusOptions = {
  DRAFT: 'Taslak',
  GENERATING: 'Üretiliyor',
  VALIDATING: 'Doğrulanıyor',
  COMPLETED: 'Tamamlandı',
  FAILED: 'Başarısız',
  CANCELLED: 'İptal Edildi',
  ACTIVE: 'Aktif',
  PASSIVE: 'Pasif',
  ARCHIVED: 'Arşivlendi',
} as const

/** Canlı ilerleme faz etiketleri (backend progress_stage → insan-okur). */
export const progressStageLabels: Record<string, string> = {
  generating: 'Sorular üretiliyor',
  generated: 'Üretim tamamlandı',
  validating: 'Doğrulanıyor',
  check_category: 'Kategori kontrolü',
  check_similarity: 'Benzerlik taraması',
  rerank_similarity: 'Kopya teyidi',
  quality_check: 'Kalite kontrolü',
  verify_correctness: 'Doğruluk denetimi',
  revise: 'Düzeltme',
  finalize: 'Sonuçlandırma',
  arbitrate: 'Gri-bölge hakemi',
  indexing: 'Kaydediliyor',
  done: 'Bitti',
  failed: 'Başarısız',
  cancelled: 'İptal edildi',
}

/** Üretim grubu kökeni — liste filtresi (Hepsi/Taslak/Özel/Periyodik). */
export const batchKindOptions = {
  INDIVIDUAL: 'Özel',
  DRAFT: 'Taslak',
  PERIODIC: 'Periyodik',
} as const

/** Liste filtre dropdown'u: tümü + köken. `ALL` filtresizdir. */
export const batchKindFilterOptions = {
  ALL: 'Hepsi',
  DRAFT: 'Taslak',
  INDIVIDUAL: 'Özel',
  PERIODIC: 'Periyodik',
} as const

export type TBatchKindFilter = keyof typeof batchKindFilterOptions

/** Batch istatistik başlıkları — projeye özgü anlamları (tooltip). Başlıklar tek
 * başına belirsiz olduğu için inceleme ekranında balon ipucu olarak gösterilir. */
export const batchStatTooltips = {
  total: 'Bu üretim grubunda AI tarafından üretilen tüm sorular (her durumda).',
  approved:
    'İncelemeci tarafından onaylanmış sorular — oyuna alınmaya (yayımlanmaya) hazır.',
  rejected:
    'İncelemeci tarafından reddedilmiş sorular — oyuna alınmaz, havuza eklenmez.',
  revision: 'Düzeltme/yeniden gözden geçirme bekleyen sorular.',
  active:
    'Grup "Aktif" durumdayken oyunda canlı olan onaylı soru sayısı. Grup pasifken 0 görünür.',
  passive:
    'Grup "Pasif" durumdayken oyundan geçici kaldırılmış onaylı soru sayısı. Grup aktifken 0 görünür.',
} as const

/** Üretilen soru inceleme durumları. PDF 11.2. */
export const reviewStatusOptions = {
  DRAFT: 'İncelemede',
  APPROVED: 'Onaylandı',
  REVISION: 'Revizyon',
  REJECTED: 'Reddedildi',
} as const

/** Sistem önerisi. PDF 9.5. */
export const suggestionOptions = {
  APPROVE: 'Uygun',
  REVISE: 'Revize edilmeli',
  REJECT: 'Reddedilmeli',
  MANUAL: 'Manuel inceleme gerekli',
} as const

/** Risk işaretleri. PDF 7.8 / 13.1 / 16. */
export const riskFlagOptions = {
  TIME_SENSITIVE: 'Zamana bağlı',
  SENSITIVE_CONTENT: 'Hassas içerik',
  AMBIGUOUS: 'Anlaşılması zor',
  WEAK_DISTRACTORS: 'Zayıf çeldiriciler',
} as const

/** Soru adedi seçenekleri (dropdown). */
export const GENERATION_COUNT_OPTIONS = [25, 50, 75] as const

export const MAX_GENERATION_COUNT = 100
export const MIN_GENERATION_COUNT = 1

/**
 * Otomatik AI doğrulama/onay — her aksiyon (onayla/revize/reddet) BAĞIMSIZ
 * yapılandırılır. Hiçbiri açık değilse tüm sorular incelemede kalır (mevcut
 * davranış). NOT: arayüz hazır; otomatik uygulama backend tarafı tamamlandığında
 * devreye girer.
 */

/** Doğruluk/güven eşiği sınırları (%). img1 md.5: "%75 üstü gibi". */
export const MIN_CONFIDENCE_THRESHOLD = 50
export const MAX_CONFIDENCE_THRESHOLD = 100

/** Otomatik onay için varsayılan minimum doğruluk güveni (%). */
export const DEFAULT_APPROVE_CONFIDENCE = 75
/** Otomatik reddetme için varsayılan minimum güven (%) — onaydan daha temkinli. */
export const DEFAULT_REJECT_CONFIDENCE = 85

/** AI'ın bir soruyu en fazla kaç kez yeniden yazmayı deneyeceği. */
export const REVISE_ATTEMPT_OPTIONS = [1, 2, 3] as const
export const DEFAULT_REVISE_MAX_ATTEMPTS = 1

/**
 * Doğrulama (AI judge) titizliği — judge'ın GÜCÜNÜ ayarlar (model katmanı + kör-çöz
 * oy sayısı), ham model seçimini DEĞİL. Üretici≠denetçi korunur (judge daima üretimden
 * ayrı, sabit bulut sağlayıcıdır). Backend alanı: `verification_rigor`.
 */
export const verificationRigorOptions = {
  FAST: 'Hızlı',
  BALANCED: 'Dengeli',
  THOROUGH: 'Titiz',
} as const

/** Her titizlik seviyesinin kullanıcıya gösterilen açıklaması (Select altı not). */
export const verificationRigorDescriptions = {
  FAST: 'Zayıf model + tek oy. En hızlı/ucuz, en düşük güvence — otomatik onayla birlikte kullanılamaz.',
  BALANCED: 'Orta model + 3 oy. Önerilen denge (mevcut varsayılan davranış).',
  THOROUGH: 'Güçlü model + 5 oy. En yüksek güvence; daha yavaş ve maliyetli.',
} as const

/** Varsayılan doğrulama titizliği (geriye uyumlu: orta model + 3 oy). */
export const DEFAULT_VERIFICATION_RIGOR = 'BALANCED'
